/**
 * Adaptador do `esptool-js`: o único arquivo que importa a biblioteca, e com
 * `import()` dinâmico — os stubs de flasher pesam, e só quem grava paga.
 *
 * O resto do gravador fala com `Carregador`, que o teste troca por um falso.
 */
import SparkMD5 from 'spark-md5'
import type { PortaSerial } from './serial'

export type Regiao = { endereco: number; dados: Uint8Array }

export interface Carregador {
  /** `CHIP_NAME` detectado pela ROM — "ESP32" para o robô. */
  chip(): string
  /** MAC base lido do eFuse, em minúsculas com dois-pontos. */
  mac(): Promise<string>
  /** Escreve e VERIFICA (MD5 calculado pelo chip contra o daqui). */
  gravar(regioes: Regiao[], aoProgredir: (feitos: number, total: number) => void): Promise<void>
  reiniciar(): Promise<void>
  /** Solta a porta. Depois disso a porta pode ser reaberta para ler linhas. */
  fechar(): Promise<void>
}

/** MD5 em hexa, como o `flashMd5sum` da ROM devolve. */
export function md5Hex(dados: Uint8Array): string {
  return SparkMD5.ArrayBuffer.hash(
    dados.buffer.slice(dados.byteOffset, dados.byteOffset + dados.byteLength) as ArrayBuffer)
}

/**
 * Hard reset pelos sinais da porta: EN (RTS) em baixo com IO0 (DTR) solto, e
 * solta — a placa sai da ROM e sobe o app. É o que `after('hard_reset')` faz,
 * mas sem o loader: serve para quando o sync falhou e não há loader
 * conectado (TASK-11986). As escritas de sinal continuam valendo com a
 * leitura morta (medido em 29/09). Nunca levanta: é a última coisa antes de
 * soltar a porta, e o erro que importa é o do sync.
 */
export async function soltarDaRom(
  porta: PortaSerial,
  esperar: (ms: number) => Promise<void> = (ms) => new Promise((ok) => setTimeout(ok, ms)),
): Promise<void> {
  if (!porta.setSignals) return
  try {
    await porta.setSignals({ dataTerminalReady: false, requestToSend: true })
    await esperar(100)
    await porta.setSignals({ dataTerminalReady: false, requestToSend: false })
  } catch {
    // porta que se foi de vez: não há placa a soltar
  }
}

export async function abrirCarregador(porta: PortaSerial): Promise<Carregador> {
  const { ESPLoader, Transport } = await import('esptool-js')
  // O tipo `SerialPort` da biblioteca é o do DOM; a nossa `PortaSerial` é o
  // subconjunto dele que o resto do módulo usa.
  const transport = new Transport(porta as unknown as ConstructorParameters<typeof Transport>[0], false)
  const loader = new ESPLoader({ transport, baudrate: 460800, romBaudrate: 115200 })
  try {
    await loader.main('default_reset')
  } catch (e) {
    // O reset de entrada deixou a placa em `waiting for download`; sem este
    // pulso ela fica lá até alguém mexer no cabo (o Izy ficou 4h30).
    await soltarDaRom(porta)
    await transport.disconnect().catch(() => {})
    throw e
  }
  return {
    chip: () => loader.chip.CHIP_NAME,
    mac: async () => (await loader.chip.readMac(loader)).toLowerCase(),
    gravar: async (regioes, aoProgredir) => {
      const total = regioes.reduce((n, r) => n + r.dados.length, 0)
      const antes = regioes.map((_, i) => regioes.slice(0, i).reduce((n, r) => n + r.dados.length, 0))
      await loader.writeFlash({
        fileArray: regioes.map((r) => ({ data: r.dados, address: r.endereco })),
        // `keep`: não reescrever cabeçalho nem medir a flash. O que muda é só
        // o app e o otadata; o resto da placa fica como está.
        flashMode: 'keep', flashFreq: 'keep', flashSize: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (i, escritos, totalDoArquivo) => {
          // O progresso vem em bytes COMPRIMIDOS por arquivo; basta a fração.
          const fracao = totalDoArquivo > 0 ? escritos / totalDoArquivo : 1
          aoProgredir(Math.round(antes[i] + fracao * regioes[i].dados.length), total)
        },
        calculateMD5Hash: md5Hex,
      })
    },
    reiniciar: () => loader.after('hard_reset'),
    fechar: () => transport.disconnect(),
  }
}
